import { flow, types } from 'mobx-state-tree';
import { apiService } from '@services/apiService';
import { withAlertStore } from '@stores/alert/withAlertStore';
import { withMainStore } from '@stores/main/withMainStore';
import { withProfileStore } from '@stores/profile/withProfileStore';
import { IChangeStatus, ICountryCodeData } from '@/types/common';
import { IBrandFormWithFiles } from '@forms/BrandForm/types';
import { IAddBrandFormReq, IBrandsMarker, IBusinessBrandData } from './types';
import { filterReqData } from '@/utils/prepareData';

const { model, maybeNull, frozen } = types;

export const BrandsStore = model('BrandsStore', {
  brandsList: maybeNull(frozen<IBusinessBrandData[]>()),
  brand: maybeNull(frozen<IBusinessBrandData>()),
  markers: maybeNull(frozen<IBrandsMarker[]>()),
  countryCodes: maybeNull(frozen<ICountryCodeData[]>()),
})
  .extend(withAlertStore)
  .extend(withMainStore)
  .extend(withProfileStore)
  .views((self) => ({}))
  .actions((self) => ({
    setBrandsList(data: IBusinessBrandData[]) {
      self.brandsList = data;
      return data;
    },
    setBrand(data: IBusinessBrandData | null) {
      self.brand = data;
      return data;
    },
    setMarkers(data: IBrandsMarker[]) {
      self.markers = data;
      return data;
    },
    setCountryCodes(data: ICountryCodeData[]) {
      self.countryCodes = data;
      return data;
    },
  }))
  .actions((self) => ({
    afterCreate() {},
    getBrandsList: flow(function* () {
      return yield apiService
        .get('brand-list')
        .then(({ data: { data } }) => {
          return self.setBrandsList(data);
        })
        .catch(self.mainStore.errorHandling);
    }),
    getBrand: flow(function* (id: string) {
      return yield apiService
        .get(`brand/show/${id}`)
        .then(({ data: { data } }) => {
          return self.setBrand(data);
        })
        .catch(self.mainStore.errorHandling);
    }),
    getBrandsMarkers: flow(function* (countryCode: string) {
      return yield apiService
        .get(`brands-default-markers?countryCode=${countryCode}`)
        .then(({ data: { data } }) => {
          return self.setMarkers(data.markers);
        })
        .catch(self.mainStore.errorHandling);
    }),
    getCountryCodes: flow(function* () {
      return yield apiService
        .get('country-code/list')
        .then(({ data: { data } }) => {
          return self.setCountryCodes(data);
        })
        .catch(self.mainStore.errorHandling);
    }),
    addBrand: flow(function* (data: IBrandFormWithFiles) {
      const { countryCodeData, logoUrlFile, ...filtred } = data;
      const newData: IAddBrandFormReq = filterReqData({
        ...filtred,
        countryCode: countryCodeData.countryCode,
      });
      return yield apiService
        .post('brand/create', newData)
        .then(() => {
          const { user, getProfile } = self.profileStore;
          getProfile();
          if (user?.id) {
            self.mainStore.setSelectedCountryCode({
              id: user.id,
              countryCodeData,
            });
          }
        })
        .catch(self.mainStore.errorHandling);
    }),
    editBrand: flow(function* (data: IBusinessBrandData & IBrandFormWithFiles) {
      const { countryCodeData, logoUrlFile, ...filtred } = data;
      const newData: IBusinessBrandData = filterReqData({
        ...filtred,
        countryCode: countryCodeData.countryCode,
      });
      return yield apiService
        .patch(`brand/update/${data.id}`, newData)
        // .then(({ data: { data: newBrand } }) => {})
        .catch(self.mainStore.errorHandling);
    }),
    changeStatus: flow(function* ({ id, status }: IChangeStatus) {
      return yield apiService
        .patch(`brand/update-status/${id}`, { status })
        .then(({ data: { data } }) => {
          return self.setBrand(data);
        })
        .catch(self.mainStore.errorHandling);
    }),
  }));
